import { ErrorHandler, inject, Injectable } from '@angular/core';
import { WA_WINDOW } from '@ng-web-apis/common';
import type { Logger } from '@posthog/core';
import { BucketedRateLimiter, resolveExceptionRateLimiterConfig } from '@posthog/core/utils';
import { AssetLoadError } from '@rma/generic/util/dynamic-asset-loader';
import { HardNavigationService } from './hard-navigation.service';
import { httpExceptionForCapture, isExpectedHttpFailure, isHttpErrorResponse } from './http-error-reporting';
import { PageLeavingService } from './page-leaving.service';
import { PostHogService } from './posthog.service';
import { POSTHOG_EXCEPTION_CONTEXT } from './posthog-exception-context.token';

const STALE_CHUNK_MESSAGES = [
  /ChunkLoadError: Loading chunk/,
  /Importing a module script failed/,
  /Failed to fetch dynamically imported module/,
];

// Matched against the same description as the stale-chunk check, which is prefixed with the thrown
// value's name, so most are unanchored on the left.
export const NON_ACTIONABLE_ERROR_PATTERNS: readonly RegExp[] = [
  /Script error\./,
  /Error invoking postMessage: Java object is gone/,
  /Object Not Found Matching Id:/,
  /TypeError:.*bird_MV/,
  // Anchored so only an error named AbortError matches, not one whose message mentions it.
  /^AbortError(?::|\s|$)/,
];

// Fetches that the browser cancels while the page unloads. The lookahead keeps a failed lazy-route
// import ("Failed to fetch dynamically imported module") out, since that one triggers the reload.
const NAVIGATION_ABORT_PATTERNS: readonly RegExp[] = [
  /TypeError: Load failed/,
  /TypeError: Failed to fetch(?! dynamically)/,
  /LaunchDarklyFlagFetchError/,
];

// Keyed by pathname alone because the query can carry a one-time token (/login/token).
const STALE_CHUNK_RELOADED_KEY_PREFIX = 'rma.posthog.staleChunkReloaded:';
const EXCEPTION_RATE_LIMIT_REFILL_INTERVAL_MS = 10_000;

// The limiter wants a logger and the handler has nothing worth logging.
const SILENT_LOGGER: Logger = {
  debug: () => undefined,
  info: () => undefined,
  warn: () => undefined,
  error: () => undefined,
  critical: () => undefined,
  createLogger: () => SILENT_LOGGER,
};

/** Angular hands the handler whatever was thrown, which is not always an Error. */
// eslint-disable-next-line no-restricted-syntax -- the whole point of the function is that Angular hands the handler whatever was thrown
function describe(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  // SAFETY(legacy): unreviewed, pre-dates this rule.
  const candidate = value as { message?: unknown; name?: unknown } | null | undefined;

  return [candidate?.name, candidate?.message].filter((part) => typeof part === 'string').join(': ');
}

// posthog-js types a thrown value by its name and falls back to 'Error' when it has none.
// eslint-disable-next-line no-restricted-syntax -- the thrown value is untyped
function exceptionType(reported: unknown): string {
  // SAFETY: only reads `name`, and only uses it when it is a non-empty string.
  const name = (reported as { name?: unknown } | null | undefined)?.name;

  return typeof name === 'string' && name ? name : 'Error';
}

@Injectable()
export class PostHogErrorHandler implements ErrorHandler {
  private readonly posthogService = inject(PostHogService);
  private readonly window = inject<Window>(WA_WINDOW);
  private readonly hardNavigation = inject(HardNavigationService);
  private readonly pageLeaving = inject(PageLeavingService);
  private readonly context = inject(POSTHOG_EXCEPTION_CONTEXT, { optional: true });

  // client.captureException is never rate-limited, so a render loop would send every iteration.
  // Reuses the per-type limiter posthog-js applies to its own window capture, with its defaults.
  private readonly rateLimiter = new BucketedRateLimiter<string>({
    ...resolveExceptionRateLimiterConfig(),
    refillInterval: EXCEPTION_RATE_LIMIT_REFILL_INTERVAL_MS,
    _logger: SILENT_LOGGER,
  });

  // The document stays alive until the reload lands, so a second chunk failure can arrive first.
  private reloadRequested = false;

  public handleError(error: unknown): void {
    // SAFETY(legacy): unreviewed, pre-dates this rule.
    const originalError = (error as { originalError?: unknown } | null | undefined)?.originalError;
    // Zone.js wraps an unhandled promise rejection in an Error and keeps the rejected value on `rejection`.
    // Only an HTTP error is unwrapped, so every other rejection is handled as the wrapper it arrives in.
    // SAFETY: only reads `rejection`, and only uses it when the type guard accepts it.
    const rejection = (error as { rejection?: unknown } | null | undefined)?.rejection;
    const reported = originalError ?? (isHttpErrorResponse(rejection) ? rejection : error);

    // Ad blockers routinely stop third-party scripts, so a failed load is an event to count
    // rather than an exception to alert on.
    if (reported instanceof AssetLoadError) {
      this.posthogService.capture('AssetLoadFailed', { url: reported.url });

      return;
    }

    // Read both: Angular's wrapper carries the message, the original carries the name, and a chunk
    // failure can present as either depending on how it was thrown.
    const description = `${describe(error)} ${describe(originalError)}`;

    // Dropped before the rate limiter so that noise cannot use up the burst a real error needs.
    if (isExpectedHttpFailure(reported) || this.isNonActionable(reported, description)) {
      console.error(error);

      return;
    }

    if (!STALE_CHUNK_MESSAGES.some((pattern) => pattern.test(description))) {
      this.report(reported, error);

      return;
    }

    // The leaving mark clears if the reload is cancelled, so later failures are reported rather than lost.
    if (this.reloadRequested && this.pageLeaving.isLeaving()) {
      return;
    }

    // A deploy replaces the hashed chunks, so a session that started on the old build 404s on its
    // next lazy route. The first failure on a pathname reloads silently to pick up the new index.
    if (this.markStaleChunkReload()) {
      this.reloadRequested = true;
      this.hardNavigation.reload();

      return;
    }

    // The reload did not cure it, or storage is unusable, so this is a real fault.
    this.report(reported, error, { stale_chunk: true });
  }

  // eslint-disable-next-line no-restricted-syntax -- the thrown value is untyped
  private isNonActionable(reported: unknown, description: string): boolean {
    // A script blocked by an ad blocker rejects with the DOM Event itself, which carries no stack or message.
    if (reported instanceof Event && !(reported instanceof ErrorEvent)) {
      return true;
    }

    if (NON_ACTIONABLE_ERROR_PATTERNS.some((pattern) => pattern.test(description))) {
      return true;
    }

    const leaving = this.pageLeaving.isLeaving() || this.window.document.visibilityState === 'hidden';

    return leaving && NAVIGATION_ABORT_PATTERNS.some((pattern) => pattern.test(description));
  }

  // Without a record of the last reload, a new build that is also broken would reload forever, so
  // unreadable storage returns false. Returns true only when this call set the flag.
  private markStaleChunkReload(): boolean {
    try {
      const storage = this.window.sessionStorage;
      const key = `${STALE_CHUNK_RELOADED_KEY_PREFIX}${this.window.location.pathname}`;

      if (storage.getItem(key) !== null) {
        return false;
      }

      storage.setItem(key, '1');

      return true;
    } catch {
      return false;
    }
  }

  // eslint-disable-next-line no-restricted-syntax -- both the unwrapped and the original throwable reach PostHog untyped, for the same reason
  private report(reported: unknown, original: unknown, extraProperties?: Record<string, unknown>): void {
    const client = this.posthogService.client;

    if (!client) {
      console.error(original);
      return;
    }

    // Prefixed so a thrown name like `__proto__` cannot reach a prototype member of the bucket map.
    if (this.rateLimiter.consumeRateLimit(`type:${exceptionType(reported)}`)) {
      return;
    }

    const context = this.context?.();
    const properties = extraProperties ? { ...context, ...extraProperties } : context;

    const http = httpExceptionForCapture(reported, properties);

    if (http) {
      client.captureException(http.exception, http.properties);

      return;
    }

    // Absent rather than undefined, so an app with no context reports exactly as before.
    if (properties) {
      client.captureException(reported, properties);

      return;
    }

    client.captureException(reported);
  }
}
